"use client";

import { useId, type CSSProperties, type InputHTMLAttributes } from "react";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label?: string;
  error?: string;
  /** RN-convention change handler — receives the string value directly. */
  onChangeText?: (value: string) => void;
  className?: string;
  style?: CSSProperties;
}

export default function Input({
  label,
  error,
  onChangeText,
  id,
  className = "",
  style,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  ...rest
}: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = error && inputId ? `${inputId}-error` : undefined;
  return (
    <div>
      {label && (
        <label
          htmlFor={inputId}
          className="mb-2 block text-body-small font-semibold text-text-primary"
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        aria-invalid={error ? true : invalid}
        aria-describedby={[describedBy, errorId].filter(Boolean).join(" ") || undefined}
        onChange={(e) => onChangeText?.(e.target.value)}
        className={
          "input-sacred min-h-11 w-full rounded-lg px-4 py-3 text-body text-text-primary " +
          (error ? "border-intent-error " : "") +
          className
        }
        style={style}
        {...rest}
      />
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-caption text-intent-error">
          {error}
        </p>
      )}
    </div>
  );
}
